import { forwardRef, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Checkbox,
  Collapse,
  Grid,
  Group,
  Paper,
  Select,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
  UnstyledButton
} from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useGetServicesToDeliverReport } from '../../../hooks/reports/queries';
import { ServicesToDeliverDetailItem, ServicesToDeliverSummaryItem } from '.';
import TableView, { buildColumns } from './TableView';
import useDownloadExcel from '../../../hooks/useDownloadExcel/useDownloadExcel';
import {
  IconChevronDown,
  IconChevronRight,
  IconFileSpreadsheet,
  IconInfoCircle,
  IconPrinter
} from '@tabler/icons-react';
import { exportServicesToDeliverReportCSV } from '../../../services/reports.service';
import { useGetStores } from '../../../hooks/stores/queries';
import { useReactToPrint } from 'react-to-print';

type PrintableColumn = ReturnType<typeof buildColumns>[number];

type PrintableTableProps = {
  rows: ServicesToDeliverDetailItem[];
  columns: PrintableColumn[];
  t: ReturnType<typeof useTranslation>['t'];
  headerInfo: {
    title: string;
    dateLabel: string;
    storeLabel?: string;
  };
};

const PrintableServicesToDeliverTable = forwardRef<HTMLDivElement, PrintableTableProps>(
  ({ rows, columns, t, headerInfo }, ref) => {
    const renderCell = (col: PrintableColumn, row: ServicesToDeliverDetailItem) => {
      const raw = col.accessorFn(row);
      if (col.id === 'lineStatus') {
        const value = (raw as string) || '';
        return t(`FieldName:orderStatuses.${value}`, { defaultValue: value });
      }
      return raw as any;
    };
    return (
      <div ref={ref} className="print-only">
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{headerInfo.title}</div>
          <div style={{ fontSize: 12 }}>{headerInfo.dateLabel}</div>
          {headerInfo.storeLabel ? (
            <div style={{ fontSize: 12 }}>{headerInfo.storeLabel}</div>
          ) : null}
        </div>
        <table className="print-table">
          <thead>
            <tr>
              <th>#</th>
              {columns.map((col, idx) => (
                <th key={`head-${idx}`}>{col.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={`${row.orderId || idx}-${idx}`}>
                <td>{idx + 1}</td>
                {columns.map((col, colIdx) => (
                  <td key={`cell-${idx}-${colIdx}`}>{renderCell(col, row)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
);
PrintableServicesToDeliverTable.displayName = 'PrintableServicesToDeliverTable';

const formatQty = (value: number) =>
  new Intl.NumberFormat('es-MX', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(value || 0);

export default function ServicesToDeliverReportContent({
  companyId,
  storeId,
  initialDateRange,
  showStoreSelect = false
}: {
  companyId: string;
  storeId: string;
  initialDateRange?: DatesRangeValue;
  showStoreSelect?: boolean;
}) {
  const { t } = useTranslation(['FieldName', 'CommonButtons', 'ReportsPage']);
  const { isLoading: isGetStoresLoading, data: stores } = useGetStores(showStoreSelect);
  const [selectedStoreId, setSelectedStoreId] = useState<string>(storeId || 'all');

  const [dateRange, setDateRange] = useState<DatesRangeValue>(
    initialDateRange || [moment().toDate(), moment().toDate()]
  );
  const [hideReady, setHideReady] = useState<boolean>(false);
  const [summaryOpen, setSummaryOpen] = useState<boolean>(false);

  const { downloadExcelV2, loadingExport } = useDownloadExcel();

  const effectiveStoreId = showStoreSelect ? selectedStoreId : storeId;

  const isMultiStore = (effectiveStoreId || 'all') === 'all';
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    storeName: isMultiStore,
    customerPhone: false,
    total: false,
    balance: false
  });

  const printableColumns = useMemo(() => buildColumns(t), [t]);
  const visiblePrintableColumns = useMemo(
    () => printableColumns.filter((col) => columnVisibility[col.id ?? ''] !== false),
    [printableColumns, columnVisibility]
  );

  const printRef = useRef<HTMLDivElement>(null);

  const queryParams = useMemo(() => {
    const [from, to] = dateRange || [];
    return {
      storeId: effectiveStoreId || 'all',
      dateFrom: from ? moment(from).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
      dateTo: to ? moment(to).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
      hideReady: hideReady ? 'true' : 'false'
    };
  }, [effectiveStoreId, dateRange, hideReady]);

  const { data, isLoading, isError } = useGetServicesToDeliverReport(companyId, queryParams);

  const summaryServices: ServicesToDeliverSummaryItem[] = data?.summary?.services || [];
  const totals = data?.summary?.totals || { servicesCount: 0, linesCount: 0, ordersCount: 0 };
  const items: ServicesToDeliverDetailItem[] = data?.items || [];

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Reporte_Servicios_Por_Entregar_${queryParams.storeId}_${queryParams.dateFrom}_${queryParams.dateTo}`
  });

  const printHeaderInfo = useMemo(() => {
    const dateLabel =
      queryParams.dateFrom === queryParams.dateTo
        ? `${t('FieldName:date')}: ${queryParams.dateFrom}`
        : `${t('FieldName:date')}: ${queryParams.dateFrom} — ${queryParams.dateTo}`;
    let storeLabel: string | undefined;
    if (showStoreSelect) {
      const selected = (stores || []).find((s) => s._id === effectiveStoreId);
      storeLabel = `${t('FieldName:store')}: ${
        effectiveStoreId === 'all' || !selected
          ? t('FieldName:allStores') || 'Todas'
          : selected.name || ''
      }`;
    }
    return {
      title: t('ReportsPage:servicesToDeliverReport.title'),
      dateLabel,
      storeLabel
    };
  }, [queryParams, showStoreSelect, stores, effectiveStoreId, t]);

  const storeSelectData = useMemo(() => {
    if (!showStoreSelect) return [];
    const allOption = { label: t('FieldName:allStores') || 'Todas', value: 'all' };
    const storeOptions =
      stores
        ?.filter((store) => store.name && store._id)
        .map((store) => ({
          label: store.name || '',
          value: store._id || ''
        })) || [];
    return [allOption, ...storeOptions];
  }, [showStoreSelect, stores, t]);

  return (
    <>
      <style>
        {`
          .print-only { display: none; }
          @media print {
            .no-print { display: none !important; }
            .print-only { display: block !important; }
            table.print-table {
              width: 100%;
              border-collapse: collapse;
            }
            table.print-table th,
            table.print-table td {
              border: 1px solid #dcdcdc;
              padding: 6px;
              font-size: 12px;
            }
            table.print-table th {
              background: #f5f5f5;
            }
          }
        `}
      </style>
      <Grid gutter="md">
        <Grid.Col span={{ xs: 12, md: 'auto' }} className="no-print">
          <DatePickerInput
            clearable={false}
            type="range"
            label={t('FieldName:date')}
            placeholder={t('FieldName:date')}
            value={dateRange}
            onChange={setDateRange}
            allowSingleDateInRange
          />
        </Grid.Col>
        {showStoreSelect && (
          <Grid.Col span={{ xs: 12, md: 'auto' }} className="no-print">
            <Select
              value={selectedStoreId}
              onChange={(val) => setSelectedStoreId(val || 'all')}
              allowDeselect={false}
              label={t('FieldName:store')}
              placeholder={t('FieldName:store')}
              data={storeSelectData}
              disabled={isGetStoresLoading}
            />
          </Grid.Col>
        )}
        <Grid.Col
          span={{ xs: 12, md: 'auto' }}
          style={{ alignSelf: 'flex-end' }}
          className="no-print">
          <Checkbox
            label={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span>{t('ReportsPage:servicesToDeliverReport.hideReady')}</span>
                <Tooltip
                  label={t('ReportsPage:servicesToDeliverReport.hideReadyTooltip')}
                  multiline
                  maw={280}
                  withArrow>
                  <ThemeIcon
                    component="span"
                    size={16}
                    radius="xl"
                    variant="transparent"
                    color="gray"
                    style={{ cursor: 'help', flexShrink: 0 }}>
                    <IconInfoCircle size={14} stroke={1.8} />
                  </ThemeIcon>
                </Tooltip>
              </span>
            }
            checked={hideReady}
            onChange={(e) => setHideReady(e.currentTarget.checked)}
          />
        </Grid.Col>
        <Grid.Col
          span={{ xs: 12, md: 'content' }}
          style={{ alignSelf: 'flex-end' }}
          className="no-print">
          <Grid justify="flex-start" gutter="xs">
            <Grid.Col span="content">
              <Tooltip label={t('CommonButtons:exportList')}>
                <ActionIcon
                  disabled={loadingExport}
                  onClick={() => {
                    downloadExcelV2(async () => {
                      return await exportServicesToDeliverReportCSV(companyId, queryParams);
                    }, `Reporte_Servicios_Por_Entregar_${queryParams.storeId}`);
                  }}
                  variant="filled"
                  color="#1D6F42"
                  aria-label="Descargar_Listado_CSV">
                  <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            </Grid.Col>
            <Grid.Col span="content">
              <Tooltip label={t('CommonButtons:print')}>
                <ActionIcon
                  onClick={handlePrint}
                  variant="filled"
                  color="blue"
                  aria-label="Imprimir_Listado">
                  <IconPrinter style={{ width: '70%', height: '70%' }} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            </Grid.Col>
          </Grid>
        </Grid.Col>

      <Grid.Col span={12}>
        <Paper withBorder p="sm" radius="md">
          <Grid gutter="xs">
            <Grid.Col span={{ xs: 12, md: 4 }}>
              <Text size="sm" c="dimmed">
                {t('ReportsPage:servicesToDeliverReport.totals.services')}
              </Text>
              <Text fw={700}>{formatQty(totals.servicesCount)}</Text>
            </Grid.Col>
            <Grid.Col span={{ xs: 12, md: 4 }}>
              <Text size="sm" c="dimmed">
                {t('ReportsPage:servicesToDeliverReport.totals.lines')}
              </Text>
              <Text fw={700}>{formatQty(totals.linesCount)}</Text>
            </Grid.Col>
            <Grid.Col span={{ xs: 12, md: 4 }}>
              <Text size="sm" c="dimmed">
                {t('ReportsPage:servicesToDeliverReport.totals.orders')}
              </Text>
              <Text fw={700}>{formatQty(totals.ordersCount)}</Text>
            </Grid.Col>
          </Grid>
        </Paper>
      </Grid.Col>

      <Grid.Col span={12}>
        <Paper withBorder p="sm" radius="md">
          <UnstyledButton onClick={() => setSummaryOpen((o) => !o)} style={{ width: '100%' }}>
            <Group justify="space-between" mb={summaryOpen ? 'xs' : 0}>
              <Text fw={600}>{t('ReportsPage:servicesToDeliverReport.summaryTitle')}</Text>
              {summaryOpen ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
            </Group>
          </UnstyledButton>
          <Collapse in={summaryOpen}>
            {summaryServices.length === 0 ? (
              <Text size="sm" c="dimmed">
                {t('ReportsPage:servicesToDeliverReport.emptySummary')}
              </Text>
            ) : (
              <Grid gutter="xs">
                {summaryServices.map((s, idx) => (
                  <Grid.Col
                    key={`${s.sectionId || 'none'}-${s.serviceName}-${s.selectedOptionLabel}-${idx}`}
                    span={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Paper withBorder p="xs" radius="sm">
                      <Stack gap={2}>
                        <Tooltip label={s.displayName} disabled={s.displayName.length < 40}>
                          <Text size="sm" fw={600} lineClamp={1}>
                            {s.displayName}
                          </Text>
                        </Tooltip>
                        <Text size="lg" fw={700}>
                          {formatQty(s.totalQuantity)} {s.unitLabel}
                        </Text>
                        <Group gap={6}>
                          <Badge size="xs" variant="light">
                            {s.linesCount}{' '}
                            {t('ReportsPage:servicesToDeliverReport.linesShort')}
                          </Badge>
                          <Badge size="xs" variant="light" color="grape">
                            {s.ordersCount}{' '}
                            {t('ReportsPage:servicesToDeliverReport.ordersShort')}
                          </Badge>
                        </Group>
                      </Stack>
                    </Paper>
                  </Grid.Col>
                ))}
              </Grid>
            )}
          </Collapse>
        </Paper>
      </Grid.Col>

      <Grid.Col span={12}>
        <TableView
          tableInfoData={items}
          totalRowsCount={items.length}
          isError={isError}
          isLoading={isLoading}
          isMultiStore={queryParams.storeId === 'all'}
          columnVisibility={columnVisibility}
          setColumnVisibility={setColumnVisibility}
        />
      </Grid.Col>
    </Grid>
    <PrintableServicesToDeliverTable
      ref={printRef}
      rows={items}
      columns={visiblePrintableColumns}
      t={t}
      headerInfo={printHeaderInfo}
    />
    </>
  );
}
